import React, { useEffect } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';

const WorkWithUs: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    AOS.init({ duration: 1200, once: true });
  }, []);

  return (
    <div className="flex flex-1 flex-col justify-center items-center bg-dark-950 pink-text">
      {/* Hero Section */}
      <section className="w-full bg-dark-950 mt-20 py-16 px-4 md:px-8">
        <div className="max-w-6xl mx-auto text-center" data-aos="fade-up">
          <h1 className="text-5xl lg:text-7xl font-oswald font-bold mb-6 pink-text tracking-wider">
            Jobba hos oss
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto text-white/70 font-oswald font-light">
            På Bando Collective söker vi ständigt ny talang och nya perspektiv. Vi har ambitiösa mål att bli en ledande aktör på sociala medier, och du har nu chansen att vara med på den resan.
          </p>
        </div>
      </section>
      
      {/* Main Content Container */}
      <div className="max-w-7xl w-full px-4 md:px-8 py-12">
        
        {/* Introduction Section */}
        <section className="rounded-2xl bg-dark-900 md:p-8 p-2 md:p-12 mb-16 shadow-xl" data-aos="fade-up">
          <div className="bg-dark-800 p-6 rounded-xl shadow-inner">
            <p className="text-lg md:text-xl leading-relaxed text-white/70 font-oswald font-light">
              Vi söker någon som vill satsa långsiktigt och är redo att ta på sig framtida ledande roller i företaget. Hos oss får du möjligheten att arbeta med talangfulla artister och bli en del av en organisation som värdesätter din unika bakgrund och kompetens. Vi tror att våra olikheter gör oss starkare tillsammans. Rollen inleds som en praktikplats i 3 månader, med möjlighet till anställning vid goda resultat.
            </p>
            
            <div className="mt-8">
              <h3 className="text-2xl md:text-3xl font-oswald font-bold mb-4 pink-text">Vi Söker:</h3>
              <p className="text-lg md:text-xl leading-relaxed text-white/70 font-oswald font-light">
                Vi söker ambitiösa och motiverade personer som vill vara en del av vårt växande team. Om du har en passion för musik och vill vara med och forma framtidens musikscen, vill vi höra från dig!
              </p>
            </div>
          </div>
        </section>

        {/* Positions Section */}
        <section id="positions" className="rounded-2xl bg-dark-900 md:p-8 p-2 md:p-12 mb-16 shadow-xl" data-aos="fade-up">
          <h3 className="text-3xl md:text-4xl font-oswald font-bold mb-8 pink-text text-center">
            Tillgängliga Positioner
          </h3>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div 
              data-aos="fade-up" 
              className="bg-dark-800 p-6 rounded-xl shadow-inner border border-pink-500/20 hover:border-pink-500/40 transition-all duration-300"
            >
              <h4 className="text-2xl font-oswald font-bold mb-4 pink-text">Promoter för events</h4>
              <p className="text-lg leading-relaxed mb-4 text-white/70 font-oswald font-light">
                Som promoter marknadsför du våra events via sociala medier, skapar engagemang och blir ansiktet utåt på plats. Du bygger relationer med artister, publik och partners, och säkerställer en fantastisk upplevelse för gästerna.
              </p>
              <p className="text-lg leading-relaxed text-white/70 font-oswald font-light">
                Vi söker dig med stark social närvaro, passion för musik och kreativitet. Erfarenhet av eventmarknadsföring är meriterande men inte ett krav.
              </p>
            </div>

            <div 
              data-aos="fade-up" 
              data-aos-delay="100"
              className="bg-dark-800 p-6 rounded-xl shadow-inner border border-pink-500/20 hover:border-pink-500/40 transition-all duration-300"
            >
              <h4 className="text-2xl font-oswald font-bold mb-4 pink-text">Editor (Bilder & Videos)</h4>
              <p className="text-lg leading-relaxed text-white/70 font-oswald font-light">
                Är du skicklig med Foto- och videoredigering? Vi behöver en kreativ och dedikerad editerare som kan skapa och redigera visuellt innehåll för våra artister och kampanjer.
              </p>
            </div>

            <div 
              data-aos="fade-up" 
              data-aos-delay="200"
              className="bg-dark-800 p-6 rounded-xl shadow-inner border border-pink-500/20 hover:border-pink-500/40 transition-all duration-300"
            >
              <h4 className="text-2xl font-oswald font-bold mb-4 pink-text">Musikproducent / Ljudtekniker</h4>
              <ul className="text-lg leading-relaxed text-white/70 font-oswald font-light list-disc pl-5 space-y-2">
                <li>Ansvara för inspelning, mixning och mastering av musik</li>
                <li>Arbeta nära artister</li>
                <li>Teknisk kompetens och erfarenhet av musikproduktion är ett krav</li>
                <li>Jobba med andra producenter</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Qualifications Section */}
        <section id="qualifications" className="rounded-2xl bg-dark-900 md:p-8 p-2 md:p-12 mb-16 shadow-xl" data-aos="fade-up">
          <h3 className="text-3xl md:text-4xl font-oswald font-bold mb-8 pink-text text-center">
            Kvalifikationer
          </h3>
          <div className="bg-dark-800 p-6 rounded-xl shadow-inner">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex items-start space-x-3 p-4 bg-dark-900 rounded-lg" data-aos="fade-up" data-aos-delay="100">
                <div className="text-pink-500 mt-1">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-lg text-white/70 font-oswald font-light">Stark passion för musik och musikindustrin</span>
              </div>
              <div className="flex items-start space-x-3 p-4 bg-dark-900 rounded-lg" data-aos="fade-up" data-aos-delay="150">
                <div className="text-pink-500 mt-1">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-lg text-white/70 font-oswald font-light">Förstå konceptet och visionen</span>
              </div>
              <div className="flex items-start space-x-3 p-4 bg-dark-900 rounded-lg" data-aos="fade-up" data-aos-delay="200">
                <div className="text-pink-500 mt-1">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-lg text-white/70 font-oswald font-light">Ambition och motivation att arbeta i en dynamisk miljö</span>
              </div>
              <div className="flex items-start space-x-3 p-4 bg-dark-900 rounded-lg" data-aos="fade-up" data-aos-delay="250">
                <div className="text-pink-500 mt-1">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-lg text-white/70 font-oswald font-light">Förmåga att arbeta självständigt och i team</span>
              </div>
              <div className="flex items-start space-x-3 p-4 bg-dark-900 rounded-lg" data-aos="fade-up" data-aos-delay="300">
                <div className="text-pink-500 mt-1">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-lg text-white/70 font-oswald font-light">Bra kommunikationsförmåga</span>
              </div>
              <div className="flex items-start space-x-3 p-4 bg-dark-900 rounded-lg" data-aos="fade-up" data-aos-delay="350">
                <div className="text-pink-500 mt-1">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-lg text-white/70 font-oswald font-light">Relevant erfarenhet eller utbildning inom det specifika området är ett plus</span>
              </div>
            </div>
            
            <div className="mt-12">
              <h3 className="text-3xl md:text-4xl font-oswald font-bold mb-8 pink-text text-center">
                Vi Erbjuder:
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start space-x-3 p-4 bg-dark-900 rounded-lg" data-aos="fade-up" data-aos-delay="100">
                  <div className="text-pink-500 mt-1">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-lg text-white/70 font-oswald font-light">En inspirerande och kreativ arbetsmiljö</span>
                </div>
                <div className="flex items-start space-x-3 p-4 bg-dark-900 rounded-lg" data-aos="fade-up" data-aos-delay="150">
                  <div className="text-pink-500 mt-1">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-lg text-white/70 font-oswald font-light">Möjlighet att arbeta med både lokala och internationella artister</span>
                </div>
                <div className="flex items-start space-x-3 p-4 bg-dark-900 rounded-lg" data-aos="fade-up" data-aos-delay="200">
                  <div className="text-pink-500 mt-1">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-lg text-white/70 font-oswald font-light">Utvecklingsmöjligheter inom företaget</span>
                </div>
                <div className="flex items-start space-x-3 p-4 bg-dark-900 rounded-lg" data-aos="fade-up" data-aos-delay="250">
                  <div className="text-pink-500 mt-1">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-lg text-white/70 font-oswald font-light">En unik möjlighet för dig att få en möjlighet i en industri där man sällan får chansen</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Application Section */}
        <section id="application" className="rounded-2xl bg-gradient-to-r from-purple-900/30 to-dark-900 md:p-8 p-2 md:p-12 shadow-xl text-center" data-aos="fade-up">
          <h3 className="text-3xl md:text-4xl font-oswald font-bold mb-6 pink-text">
            Ansökan
          </h3>
          <p className="text-xl mb-8 text-white/70 font-oswald font-light">
            Är du den vi söker? Skicka din ansökan med CV och personligt brev till <a href="mailto:Bandocollect@gmail.com" className="underline hover:text-purple-500 transition-colors duration-300">Bandocollect@gmail.com</a>. Märk din ansökan med den position du är intresserad av i ämnesraden.
          </p>
          <p className="text-xl mb-8 text-white/70 font-oswald font-light">
            Vi ser fram emot att höra från dig och välkomna dig till vårt team på Bando Collective.
          </p>
          <a 
            href="/form?reason=jobb"
            className="pink-bg px-8 py-4 rounded-lg text-white text-xl font-oswald font-bold inline-block transition-all duration-300 hover:scale-105 hover:shadow-lg"
            data-aos="fade-up" data-aos-delay="100"
          >
            <span className="flex items-center">
              <span>Skicka din ansökan</span>
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </span>
          </a>
        </section>
      </div>
    </div>
  );
};

export default WorkWithUs;
